/**
 * Is the board's feed still settling for its current selection? (Dan,
 * 2026-10-02: an empty board should say "Loading" until it is known to be
 * empty.) The decision is `lib/feedFilter/feedSettling` — this hook supplies
 * the clock: `changedAt` is when `selectionKey` last changed, and the check
 * is re-evaluated on a short poll while it says "settling" (ownership, acks
 * and the board's index status are not store fields a selector can watch).
 *
 * Before the first evaluation for a new selection the answer is "settling"
 * whenever a fleet is registered — an empty board must not flash "No games"
 * for a frame between the toggle and the first check — and "settled" when
 * none is (unwired views and tests keep their empty state as before).
 */
import { useEffect, useState } from 'react';
import { useMarketStore, type FeedFilterState, type FeedOwnership } from '@/stores/marketStore';
import { feedSettlingState, type FeedSettlingState } from '@/lib/feedFilter/feedSettling';
import { getBoardFeedNeedsStatus } from './useBoardFeedNeeds';

export const SETTLING_POLL_MS = 250;

function fleetRegistered(): boolean {
  const s = useMarketStore.getState() as { getFeedOwnership?: () => unknown[] };
  return typeof s.getFeedOwnership === 'function' && s.getFeedOwnership().length > 0;
}

const SETTLING: FeedSettlingState = { kind: 'settling' };
const SETTLED: FeedSettlingState = { kind: 'settled' };
const sameState = (a: FeedSettlingState, b: FeedSettlingState): boolean =>
  a.kind === b.kind && (a.kind !== 'rejected' || (b.kind === 'rejected' && a.error === b.error));

/**
 * D34 — what the empty board may say for this selection (see
 * `feedSettlingState`). The poll keeps running while the feed is unanswered
 * or rejected, so a late answer still turns the board's line into
 * "No games" (or rows arrive and the empty state goes away).
 */
export function useFeedSettlingState(
  selectionKey: string,
  sport: string | null,
  boardSourceId: string | null,
): FeedSettlingState {
  const [state, setState] = useState<{ key: string; value: FeedSettlingState } | null>(null);
  useEffect(() => {
    const changedAt = Date.now();
    let poll: ReturnType<typeof setInterval> | null = null;
    const evaluate = (): void => {
      // View tests mock the store partially; a missing accessor reads as "no fleet".
      const s = useMarketStore.getState() as Partial<{
        getFeedOwnership: () => FeedOwnership[];
        getFeedFilterStates: () => FeedFilterState[];
      }>;
      const value = feedSettlingState({
        now: Date.now(),
        changedAt,
        sport,
        feeds: typeof s.getFeedOwnership === 'function' ? s.getFeedOwnership() : [],
        states: typeof s.getFeedFilterStates === 'function' ? s.getFeedFilterStates() : [],
        board: boardSourceId ? getBoardFeedNeedsStatus(boardSourceId) : null,
      });
      setState((prev) => (prev && prev.key === selectionKey && sameState(prev.value, value) ? prev : { key: selectionKey, value }));
      if (value.kind === 'settled' && poll != null) { clearInterval(poll); poll = null; }
    };
    // First check on the next tick (not synchronously in the effect body),
    // then poll until settled.
    const first = setTimeout(evaluate, 0);
    poll = setInterval(evaluate, SETTLING_POLL_MS);
    return () => {
      clearTimeout(first);
      if (poll != null) clearInterval(poll);
    };
  }, [selectionKey, sport, boardSourceId]);
  if (state != null && state.key === selectionKey) return state.value;
  return fleetRegistered() ? SETTLING : SETTLED;
}

/** Is the board still loading for this selection? */
export function useFeedSettling(selectionKey: string, sport: string | null, boardSourceId: string | null): boolean {
  return useFeedSettlingState(selectionKey, sport, boardSourceId).kind === 'settling';
}
